/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ExtensionsPage.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Extensions list page - simplified using hooks and API modules.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo } from 'react';
import { useContextStore } from '../../store/context';
import { useNavigate } from 'react-router-dom';
import { Phone, Plus, Edit, Trash2, Download, Upload, Copy, Code, X } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useDebugMode } from '../../hooks/useDebugMode';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ImportExtensionsModal from './ImportExtensionsModal';
import { deleteExtensionById, navigateToExtensionDuplicate } from './extensionsActions';
import { useExtensionsList } from './hooks/useExtensionsList';
import { exportExtensions, fetchExtensionXml } from './api/extensionsApi';
import type { ExtensionColumn, Extension } from './types';

export default function ExtensionsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { isDebugModeActive } = useDebugMode();
  const { selectedDomain } = useContextStore();
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; extension: Extension | null }>({
    isOpen: false,
    extension: null,
  });
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [xmlModal, setXmlModal] = useState<{ isOpen: boolean; extension: Extension | null; xml: string }>({
    isOpen: false,
    extension: null,
    xml: '',
  });

  // Define columns
  const availableColumns = useMemo<ExtensionColumn[]>(() => [
    {
      key: 'extension',
      label: t('extensions.columns.extension'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: Extension) => (
        <span className="font-medium text-primary">{item?.extension || '-'}</span>
      ),
    },
    {
      key: 'name',
      label: t('extensions.columns.name'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: Extension) => item?.name || '-',
    },
    {
      key: 'contact_email',
      label: t('extensions.columns.email'),
      sortable: false,
      visible: true,
      render: (_: unknown, item: Extension) => item?.contact_email || '-',
    },
    {
      key: 'extension_type',
      label: t('extensions.columns.type'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: Extension) => (
        <span className="capitalize">
          {t(`extensions.types.${item?.extension_type}`) || item?.extension_type || '-'}
        </span>
      ),
    },
    {
      key: 'class_of_service_name',
      label: t('extensions.columns.classOfService'),
      sortable: false,
      visible: false,
      render: (_: unknown, item: Extension) => item?.class_of_service_name || '-',
    },
    {
      key: 'has_voicemail',
      label: t('extensions.columns.voicemail'),
      sortable: false,
      visible: false,
      render: (_: unknown, item: Extension) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.has_voicemail
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
          }`}>
          {item?.has_voicemail ? '✓' : '✗'}
        </span>
      ),
    },
    {
      key: 'enabled',
      label: t('extensions.columns.enabled'),
      sortable: true,
      visible: true,
      render: (_: unknown, item: Extension) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
          }`}>
          {item?.enabled ? t('common.enabled') : t('common.disabled')}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: t('extensions.columns.createdAt'),
      sortable: true,
      visible: false,
      render: (_: unknown, item: Extension) => new Date(item.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      sortable: false,
      visible: true,
    },
  ], [t, i18n.language]);

  // Use custom hook for list management
  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useExtensionsList({
    initialColumns: availableColumns,
    initialSortBy: 'extension',
    initialSortOrder: 'asc',
    initialLimit: 10,
    domainId: selectedDomain?.id,
  });

  // Handlers
  const handleCreate = useCallback(() => {
    navigate('/pbx/extensions/extensions/new');
  }, [navigate]);

  const handleEdit = useCallback((extension: Extension) => {
    navigate(`/pbx/extensions/extensions/${extension.uuid || extension.id}`);
  }, [navigate]);

  const handleDelete = useCallback((extension: Extension) => {
    setDeleteModal({ isOpen: true, extension });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteModal.extension) return;

    try {
      await deleteExtensionById(deleteModal.extension.uuid || deleteModal.extension.id);
      showSuccess(t('extensions.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, extension: null });
      refresh();
    } catch (err) {
      console.error('Error deleting extension:', err);
      showError(err instanceof Error ? err.message : t('extensions.messages.deleteError'));
    }
  }, [deleteModal.extension, refresh, showError, showSuccess, t]);

  const handleDuplicate = useCallback((extension: Extension) => {
    navigateToExtensionDuplicate(navigate, deleteModal.extension?.uuid || extension.uuid || extension.id);
  }, [navigate, deleteModal.extension]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const blob = await exportExtensions();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `extensions-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.setAttribute('style', 'display: none');

      document.body.appendChild(link);
      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      });

      showSuccess(t('extensions.messages.exportSuccess'));
    } catch (error) {
      console.error('Error exporting extensions:', error);
      showError(t('extensions.messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  }, [showError, showSuccess, t]);

  const handleImport = useCallback(() => {
    setImportModalOpen(true);
  }, []);

  const handleImportSuccess = useCallback(() => {
    refresh();
    showSuccess(t('extensions.messages.importSuccess'));
  }, [refresh, showSuccess, t]);

  const handleViewXml = useCallback(async (extension: Extension) => {
    try {
      const xmlData = await fetchExtensionXml(extension.uuid || extension.id);
      setXmlModal({
        isOpen: true,
        extension: extension,
        xml: xmlData
      });
    } catch (error) {
      console.error('Error fetching XML:', error);
      const errorMessage = error instanceof Error ? error.message : t('extensions.messages.xmlError');
      showError(errorMessage);
    }
  }, [showError, t]);

  // Render actions for each row
  const renderActions = useCallback((extension: Extension) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(extension)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>

      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(extension)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>

      {isDebugModeActive && (
        <SimpleTooltip content={t('extensions.actions.viewXml')} position="top">
          <button
            onClick={() => handleViewXml(extension)}
            className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
          >
            <Code className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </button>
        </SimpleTooltip>
      )}

      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDelete(extension)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [t, isDebugModeActive, handleEdit, handleDuplicate, handleViewXml, handleDelete]);

  return (
    <div className="p-6">
      <DataGridSimple
        data={data}
        loading={loading}
        columns={columns}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        renderActions={renderActions}
        searchPlaceholder={t('extensions.searchPlaceholder')}
        title={t('extensions.title')}
        titleIcon={<Phone className="h-5 w-5 text-primary" />}
        actions={
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('extensions.actions.export')} position="top">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{t('extensions.actions.export')}</span>
              </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('extensions.actions.import')} position="top">
              <button
                onClick={handleImport}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span className="hidden sm:inline">{t('extensions.actions.import')}</span>
              </button>
            </SimpleTooltip>
            <SimpleTooltip content={t('extensions.actions.add')} position="top">
              <button
                onClick={handleCreate}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm">
                                <Plus className="h-4 w-4" /> {t('extensions.actions.add')}
              </button>
            </SimpleTooltip>
          </div>
        }
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, extension: null })}
        onConfirm={confirmDelete}
        title={t('extensions.deleteExtension')}
        message={`${t('extensions.messages.confirmDelete')} (${deleteModal.extension?.extension})`}
        confirmText={t('extensions.actions.delete')}
        cancelText={t('extensions.actions.cancel')}
        type="danger"
      />

      <ImportExtensionsModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />

      {xmlModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('extensions.xmlViewer.title')} - {xmlModal.extension?.extension}
              </h3>
              <button
                onClick={() => setXmlModal({ isOpen: false, extension: null, xml: '' })}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="bg-muted/30 rounded-lg p-4 max-h-96 overflow-auto">
              <pre className="text-sm text-foreground whitespace-pre-wrap font-mono">
                {xmlModal.xml}
              </pre>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(xmlModal.xml);
                  showSuccess(t('extensions.messages.xmlCopied'));
                }}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
              >
                {t('extensions.actions.copyXml')}
              </button>
              <button
                onClick={() => setXmlModal({ isOpen: false, extension: null, xml: '' })}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isExporting && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl border-2 border-blue-500/20 dark:border-blue-400/20 animate-in zoom-in-95 duration-300">
            <div className="flex flex-col items-center gap-6">
              <div className="relative">
                <div className="absolute inset-0 w-24 h-24 -m-4">
                  <div className="w-full h-full rounded-full bg-blue-500/20 dark:bg-blue-400/20 animate-ping"></div>
                </div>
                <div className="relative w-20 h-20">
                  <div className="absolute inset-0 rounded-full border-4 border-blue-200 dark:border-blue-900/50"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 dark:border-t-blue-400 animate-spin" style={{ animationDuration: '1s' }}></div>
                </div>
                <div className="absolute inset-0 w-20 h-20 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full border-4 border-blue-300/30 dark:border-blue-700/30"></div>
                  <div className="absolute w-14 h-14 rounded-full border-4 border-transparent border-b-blue-500 dark:border-b-blue-300 animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                </div>
                <div className="absolute inset-0 w-20 h-20 flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded-full animate-pulse"></div>
                </div>
                <div className="absolute inset-0 w-20 h-20 flex items-center justify-center">
                  <Download className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-bounce" />
                </div>
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white animate-pulse">
                  {t('extensions.export.generating')}
                </h3>
                <div className="flex items-center justify-center gap-1">
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                {t('extensions.export.generatingDescription')}
              </p>

              <div className="w-full space-y-2">
                <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                  <div className="absolute inset-0 h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-500 dark:from-blue-400 dark:via-blue-500 dark:to-blue-400 animate-pulse"></div>
                  <div className="absolute inset-0 h-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" style={{
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 2s infinite linear'
                  }}></div>
                </div>
                <p className="text-xs text-center text-blue-600 dark:text-blue-400 font-medium animate-pulse">
                  Processing your data...
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
